/**
 * Read-model for one quiz attempt, as GET /quizzes/attempts/:id returns it.
 *
 * The API gives the raw attempt row: `score` is the number of correct answers out of
 * `totalScore` (= `totalQuestions`), `completed` is whether the attempt passed, `answers` holds
 * what the student submitted per question (questionId, submittedAnswer, correct), and the
 * questions themselves sit in `quizVersion.questions` — the version the attempt was graded
 * against. `quiz` names the lesson and carries the pass mark; `teacherFeedback` is the feedback
 * already given, if any.
 *
 * The review screens (teacher quiz feedback, quiz results modal) want questions with the student's
 * answer next to them, so join the two here once.
 */

// Same default the backend applies to a quiz authored without a pass mark.
export const DEFAULT_PASSING_SCORE = 60;

/** Score as a whole percent; 0 for an attempt with nothing to score. */
export function attemptPercent(attempt) {
  const total = attempt?.totalScore ?? attempt?.totalQuestions ?? 0;
  return total > 0 ? Math.round(((attempt?.score || 0) / total) * 100) : 0;
}

// A multiple-choice answer is the option key ("B"); show it with the option text when known.
function answerLabel(question, key) {
  if (key == null || key === '') return '';
  const options = question?.options;
  const text = options && typeof options === 'object' ? options[key] : null;
  return text ? `${key}. ${text}` : String(key);
}

/**
 * @returns {{
 *   title: string, score: number, total: number, percent: number, passed: boolean,
 *   passingScore: number, submittedAt: string|null,
 *   questions: Array<{ id: string, text: string, studentAnswer: string, correctAnswer: string, isCorrect: boolean }>
 * }}
 */
export function buildAttemptReview(attempt) {
  const answers = new Map((attempt?.answers || []).map((answer) => [answer.questionId, answer]));
  const questions = (attempt?.quizVersion?.questions || []).map((question) => {
    const answer = answers.get(question.id);
    return {
      id: question.id,
      text: question.text,
      studentAnswer: answerLabel(question, answer?.submittedAnswer),
      correctAnswer: answerLabel(question, question.correctAnswer),
      isCorrect: !!answer?.correct,
    };
  });

  return {
    title: attempt?.quiz?.lesson?.title || '',
    score: attempt?.score || 0,
    total: attempt?.totalScore ?? attempt?.totalQuestions ?? 0,
    percent: attemptPercent(attempt),
    passed: !!attempt?.completed,
    passingScore: attempt?.quiz?.passingScore ?? DEFAULT_PASSING_SCORE,
    submittedAt: attempt?.submittedAt || attempt?.createdAt || null,
    questions,
  };
}
